import { isSubscriptionPaused, type StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscriptionsBlock } from "../../../../shared/schema";

export interface ActionAvailability {
  canCancel: boolean;
  canUndoCancel: boolean;
  canPause: boolean;
  canResume: boolean;
  canUpdatePaymentMethod: boolean;
  hasAnyAction: boolean;
}

/**
 * Determines which subscription management actions are available
 * based on the subscription status and block configuration.
 *
 * Status-to-Actions Mapping:
 * - active: Cancel, Pause, UpdatePayment
 * - active + cancel_at_period_end: UndoCancel, UpdatePayment
 * - active + pause_collection: Cancel, Resume, UpdatePayment
 * - trialing: Cancel, UpdatePayment
 * - past_due: Cancel, UpdatePayment (emphasized)
 * - canceled: none
 * - unpaid: Cancel, UpdatePayment (emphasized)
 *
 * Note: Stripe's pause_collection feature keeps status as "active" but sets pause_collection.
 * We use isSubscriptionPaused() to check both pause_collection and status === "paused".
 */
export function useActionAvailability(
  subscription: StripeSubscription,
  blockProps: StripeSubscriptionsBlock["props"]
): ActionAvailability {
  const { status, cancel_at_period_end } = subscription;
  const isPaused = isSubscriptionPaused(subscription);

  // Canceled subscriptions have no available actions
  if (status === "canceled" || status === "incomplete_expired") {
    return {
      canCancel: false,
      canUndoCancel: false,
      canPause: false,
      canResume: false,
      canUpdatePaymentMethod: false,
      hasAnyAction: false,
    };
  }

  // Can cancel if: active/trialing/past_due/unpaid/paused AND not already scheduled for cancellation
  const canCancel =
    blockProps.allowCancel &&
    ["active", "trialing", "past_due", "unpaid", "paused"].includes(status) &&
    !cancel_at_period_end;

  // Can undo cancel if: active with cancel_at_period_end (and not paused)
  const canUndoCancel = blockProps.allowUndoCancel && status === "active" && cancel_at_period_end && !isPaused;

  // Can pause if: active AND not already paused AND not scheduled for cancellation
  const canPause = blockProps.allowPause && status === "active" && !isPaused && !cancel_at_period_end;

  // Can resume if: paused (via pause_collection or status)
  const canResume = blockProps.allowResume && isPaused;

  // Can update payment method for any non-terminal status
  const canUpdatePaymentMethod =
    blockProps.allowUpdatePaymentMethod &&
    ["active", "trialing", "past_due", "unpaid", "paused", "incomplete"].includes(status);

  const hasAnyAction = canCancel || canUndoCancel || canPause || canResume || canUpdatePaymentMethod;

  return {
    canCancel,
    canUndoCancel,
    canPause,
    canResume,
    canUpdatePaymentMethod,
    hasAnyAction,
  };
}

/**
 * Returns the quick action to show on the subscription card.
 * Quick actions are contextual based on subscription status.
 */
export function getQuickAction(
  subscription: StripeSubscription,
  availability: ActionAvailability
): "resume" | "update_payment" | "undo_cancel" | null {
  const { status, cancel_at_period_end } = subscription;
  const isPaused = isSubscriptionPaused(subscription);

  // Priority 1: Resume for paused subscriptions (via pause_collection or status)
  if (isPaused && availability.canResume) {
    return "resume";
  }

  // Priority 2: Update payment for past_due/unpaid
  if ((status === "past_due" || status === "unpaid") && availability.canUpdatePaymentMethod) {
    return "update_payment";
  }

  // Priority 3: Undo cancel for subscriptions scheduled to cancel
  if (cancel_at_period_end && availability.canUndoCancel) {
    return "undo_cancel";
  }

  return null;
}
